import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { POINTER_TYPE_MAP, sideBar, sideBar2 } from '../../core/constants/sidebar.const';
import { AuthService } from '../../../services/auth.service';
import { assets } from '../../core/constants/assets.const';
import { LoginResponse } from '../../models/auth.model';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { Router, NavigationEnd } from '@angular/router';
import { roles } from '../../core/constants/roles';
import { ModuleConst } from '../../core/constants/moduleConst';
import { PointerDetails } from '../../models/sideBar';
import { SidebarContextService } from '../../../services/sidebar-context.service';
import { routeMatchesMenuPath } from '@esqyre/rental-lib';

@Component({
  selector: 'rental-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss'
})
export class SidebarComponent implements OnInit, OnDestroy {
  assetsConst = assets;
  public sideBar = sideBar;
  public innerWidth = 0;
  public sidenavWidth = 5;
  subscriptionSubject$: Subscription;
  destroy$: Subject<boolean> = new Subject<boolean>();
  currentRoute: string = '';
  profileDetails: LoginResponse | null = null;
  lowerSideBar = sideBar2;

  constructor(
    public _authService: AuthService,
    private router: Router,
    private sidebarContextService: SidebarContextService
  ) {
    this.subscriptionSubject$ = this._authService.profileDetails
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: LoginResponse | null) => {
        if (res !== null) {
          this.profileDetails = res;
          this.handleSideBar(res);
        }
      });

    // Track current route for active state detection
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.currentRoute = event.urlAfterRedirects || event.url;
      }
    });
    this.currentRoute = this.router.url;
  }

  ngOnInit(): void {
    this._authService.socketPointerSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: PointerDetails[] | null) => {
        console.log(data);
        this.applyPointerIndicators(data ?? []);
      });
  }

  applyPointerIndicators(data: PointerDetails[]): void {
    const updatedSidebar = this.sideBar.map(item => ({
      ...item,
      hasPointer: false,
      pointerCount: 0,
      subMenuItems: item.subMenuItems?.map(sub => ({
        ...sub,
        hasPointer: false,
        pointerCount: 0
      }))
    }));

    data.forEach(pointer => {
      const baseConfig = POINTER_TYPE_MAP[pointer.type];

      if (!baseConfig?.sideBarId) return;

      updatedSidebar.forEach(item => {
        if (item.sideBarId === baseConfig.sideBarId) {
          if (!baseConfig.subLabel) {
            item.hasPointer = pointer.totalCount > 0;
            item.pointerCount += pointer.totalCount;
          }

          // SUBMENU
          if (baseConfig.subLabel && item.subMenuItems?.length) {
            item.subMenuItems.forEach(sub => {
              if (sub.label === baseConfig.subLabel) {
                if (pointer.totalCount > 0) {
                  sub.hasPointer = true;
                  sub.pointerCount += pointer.totalCount;

                  item.hasPointer = true;
                  item.pointerCount += pointer.totalCount;
                }
              }
            });
          }
        }
      });
    });

    this.sideBar = updatedSidebar;
  }

  increase(): void {
    this.sidenavWidth = 15;
  }
  decrease(): void {
    this.sidenavWidth = 4;
  }
  @HostListener('window', ['$event'])
  toggleSidenav(): void {
    this.innerWidth = window.innerWidth;
    if (this.innerWidth <= 1199.98) {
      this._authService.toggleSidebarView();
    }
  }

  handleSideBar(res: LoginResponse): void {
    let filteredSidebar = this.sideBar.filter(item => {
      const isAdmin = !res.accessRole?.permission?.length; // Check if permissions array is empty

      if (isAdmin) {
        return true;
      } else {
        const currentPermission = res.accessRole?.permission?.find(
          p => p.sideBarId === item.sideBarId
        );
        return (
          currentPermission &&
          (currentPermission.isView ||
            currentPermission.isAdd ||
            currentPermission.isDelete)
        );
      }
    });

    if (res.roleSubType || res.role === roles.SUBCORPORATE) {
      // STEP 2 — Remove **Create a Booking** when isAdd = false
      const bookingPermission = res.accessRole?.permission?.find(
        p => p.sideBarId === ModuleConst.ManageBookings
      );

      if (bookingPermission && !bookingPermission.isAdd) {
        filteredSidebar = filteredSidebar.map(item => {
          if (
            item.sideBarId === ModuleConst.ManageBookings &&
            item.subMenuItems?.length
          ) {
            return {
              ...item,
              subMenuItems: item.subMenuItems.filter(
                sub => sub.label !== 'Create Booking'
              )
            };
          }
          return item;
        });
      }

      this.lowerSideBar = [
        {
          label: 'Help & Support',
          path: 'contact-us',
          img: assets.ContactUsIcon,
          isSubMenu: false,
          sideBarId: ModuleConst.ManageSettings
        }
      ];
    }
    this.sideBar = filteredSidebar;
  }

  isActive(label: string): boolean {
    const menuItem = [...this.sideBar, ...this.lowerSideBar].find(item => item.label === label);
    if (!menuItem) return false;

    if (menuItem.subMenuItems) {
      return menuItem.subMenuItems.some(subItem => this.isRouteMatch(subItem.path));
    }

    return this.isRouteMatch(menuItem.path);
  }

  isSubMenuActive(path: string): boolean {
    return this.isRouteMatch(path);
  }

  private isRouteMatch(path: string): boolean {
    if (!path) return false;

    const liveRoute = this.router.url || this.currentRoute;
    const effectiveRoute = this.sidebarContextService.resolveContextUrl(liveRoute);
    return routeMatchesMenuPath(effectiveRoute, path);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
